// WonderLabs Step 0: a short, child-facing mission invitation.
// No Journey navigation, evidence or completion rules live here.
import React, { useState } from "react";
import { ActivityIndicator, Image, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from "react-native";
import professorSwanImage from "../../../assets/professor_swan_transparent.png";
import { getWonderLabsPosterUrl } from "./WonderLabsMissionHome";

export default function WonderLabsMissionWelcome({
  mission, isDarkMode = false, isReturning = false, phaseIndex = 0,
  phaseCount = 9, starting = false, onStart, onBack,
}) {
  const { width } = useWindowDimensions();
  const [imageFailed, setImageFailed] = useState(false);
  // The same mission configuration supplies the tile hook and this welcome.
  // No second copy of the learner-facing text lives in Step 0.
  const hook = mission?.story || "Your next discovery awaits!";
  const swanInvitation = mission?.swan || "Ready for a new science adventure? I will help you investigate!";
  const actionWords = mission?.invitation || "Explore · Test · Discover";
  const dark = Boolean(isDarkMode);
  const ink = dark ? "#f8fafc" : "#15304b";
  const muted = dark ? "#cbd5e1" : "#506780";
  const surface = dark ? "#172b44" : "#ffffff";
  const poster = getWonderLabsPosterUrl(mission?.posterPath);
  const compact = width < 760;

  return (
    <ScrollView style={{ flex: 1, backgroundColor: dark ? "#0f172a" : "#eef5fb" }}
      contentContainerStyle={styles.scrollContent} accessibilityLabel={`${mission?.title || "WonderLabs"} Mission Home`}>
      <View style={styles.container}>
        <Pressable accessibilityRole="button" onPress={onBack} disabled={starting}
          style={styles.backLink}>
          <Text style={[styles.backText, { color: muted }]}>‹ All adventures</Text>
        </Pressable>
        <View style={[styles.card, { backgroundColor: surface, borderColor: dark ? "#314860" : "#dbe8f2" }]}>
          <View style={[styles.artwork, compact ? { height: 230 } : { height: 300 },
            { backgroundColor: mission?.tint || "#e0f2fe" }]}>
            {poster && !imageFailed ? (
              <Image source={{ uri: poster }} resizeMode="cover"
                accessibilityLabel={`${mission?.title} mission artwork`}
                onError={() => setImageFailed(true)} style={StyleSheet.absoluteFillObject} />
            ) : (
              <Text style={styles.fallback}>{mission?.symbol || "🔎"}</Text>
            )}
            <View style={styles.artworkOverlay}>
              <Text style={styles.eyebrow}>WONDERLABS · {mission?.eyebrow || "SCIENCE MISSION"}</Text>
              <Text style={styles.artworkTitle}>{mission?.title}</Text>
            </View>
          </View>
          <View style={[styles.content, compact ? { padding: 22 } : { padding: 32 }]}>
            <Text style={[styles.hook, compact ? { fontSize: 27, lineHeight: 34 } : null, { color: ink }]}>
              {hook}
            </Text>
            <View style={[styles.swanRow, { backgroundColor: dark ? "#243e5a" : "#eaf5ff" }]}>
              <Image source={professorSwanImage} resizeMode="contain" style={styles.swanImage}
                accessibilityLabel="Professor Swan" />
              <View style={{ flex: 1 }}>
                <Text style={[styles.swanLabel, { color: mission?.accent || "#076ca8" }]}>PROFESSOR SWAN</Text>
                <Text style={[styles.swanText, { color: ink }]}>{swanInvitation}</Text>
              </View>
            </View>
            <Text style={[styles.threeWords, { color: muted }]}>{actionWords}</Text>
            {isReturning ? (
              <Text style={[styles.progress, { color: muted }]}>
                Welcome back! Your place is saved · Phase {Math.min(phaseCount, phaseIndex + 1)} of {phaseCount}
              </Text>
            ) : null}
            <Pressable onPress={onStart} disabled={starting} accessibilityRole="button"
              accessibilityLabel={isReturning ? "Continue Mission" : "Start Mission"}
              accessibilityState={{ disabled: starting }}
              style={({ pressed }) => [styles.startButton, { backgroundColor: mission?.accent || "#0759a9" },
                pressed && !starting ? { opacity: 0.85 } : null]}>
              {starting ? <ActivityIndicator color="#fff" /> : null}
              <Text style={styles.startLabel}>
                {starting ? "Opening your mission…" : isReturning ? "Continue Mission  →" : "Start Mission  →"}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollContent: { flexGrow: 1, paddingHorizontal: 18, paddingVertical: 18 },
  container: { width: "100%", maxWidth: 940, alignSelf: "center" },
  backLink: { paddingVertical: 10, alignSelf: "flex-start", marginBottom: 12 },
  backText: { fontSize: 15, fontWeight: "700" },
  card: { borderWidth: 1, borderRadius: 22, overflow: "hidden" },
  artwork: { justifyContent: "flex-end", overflow: "hidden" },
  artworkOverlay: { padding: 22, backgroundColor: "rgba(7,23,42,0.64)" },
  eyebrow: { color: "#bae6fd", fontSize: 12, fontWeight: "900", letterSpacing: 1.3 },
  artworkTitle: { color: "#ffffff", fontWeight: "900", fontSize: 31, marginTop: 5 },
  fallback: { fontSize: 83, alignSelf: "center", marginBottom: 62 },
  content: { alignItems: "center" },
  hook: { fontSize: 33, lineHeight: 42, fontWeight: "900", textAlign: "center", maxWidth: 710 },
  swanRow: { marginTop: 22, borderRadius: 18, flexDirection: "row", alignItems: "center",
    paddingVertical: 14, paddingHorizontal: 16, width: "100%", maxWidth: 760 },
  swanImage: { width: 94, height: 104, marginRight: 12 },
  swanLabel: { fontSize: 12, fontWeight: "900", letterSpacing: 0.6, marginBottom: 4 },
  swanText: { fontSize: 16, lineHeight: 24, fontWeight: "600", flexShrink: 1 },
  threeWords: { fontSize: 15, fontWeight: "700", letterSpacing: 1, marginTop: 21 },
  progress: { fontSize: 14, fontWeight: "600", marginTop: 12, textAlign: "center" },
  startButton: { minHeight: 54, minWidth: 230, marginTop: 22, borderRadius: 28,
    paddingHorizontal: 26, flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 9 },
  startLabel: { color: "#fff", fontSize: 17, fontWeight: "900", textAlign: "center" },
});
